import React from 'react';
import PropTypes from 'prop-types';

import { crossOrigin } from '../../../../constants/DomConstants';
import mediaResourcesActions from '../../../../actions/MediaResourcesActions';
import mediaResourcesReader from '../../../../store/readHelpers/MediaResourcesReader';
import storeManager from '../../../../store/StoreManager';
import utilService from '../../../../services/UtilService';

import ServiceWorkerService from '../../../../services/ServiceWorkerService';
import Image from './Image';
import settingsReader from '../../../../store/readHelpers/SettingsReader';

export const helper = {
    getNextSource(sources, url, origin) {
        const currentIndex = sources.findIndex(source => source.url === url && source.crossOrigin === origin);
        return sources[currentIndex + 1];
    },

    getSourceToTry(logicalName, sources, mediaResources = storeManager.getState().mediaResources) {
        return sources.find(({ url }) => mediaResourcesReader.isSourceCached(logicalName, url, mediaResources))
            || sources.find(({ url, origin }) => mediaResourcesReader.isSourceLoaded(logicalName, url, origin, mediaResources))
            || sources.find(({ url, origin }) => !mediaResourcesReader.isSourceErrored(logicalName, url, origin, mediaResources));
    },
};

/**
 * Used for displaying images inserted into questions:
 * - main image that can be added on top of every question
 * - for drag and drop, the image in the drag element
 */
class PlainImage extends React.Component {
    static propTypes = {
        autoScale: PropTypes.bool,
        isInteraction: PropTypes.bool.isRequired,
        isPreRender: PropTypes.bool,
        model: PropTypes.object.isRequired,
        questionObjectType: PropTypes.string,
        reportReady: PropTypes.func,
        useModelWidthAndHeight: PropTypes.bool,
        usePaperBasedImageDimensions: PropTypes.bool,
        useMCDiscreteWithOneImageMaxWidth: PropTypes.bool,
    };

    static defaultProps = {
        reportReady: () => undefined,
    };

    constructor(props) {
        super(props);
        const { data, url, alternativeUrl, logicalName } = props.model;
        if (data) {
            this.state = {
                imageSrc: data,
            };
        } else {
            const sources = [url, alternativeUrl].reduce((accu, urlItem) => {
                if (!urlItem) {
                    return accu;
                }
                if (!utilService.isSameOrigin(urlItem)) {
                    accu.push({ url: urlItem, crossOrigin: crossOrigin.ANONYMOUS });
                }
                accu.push({ url: urlItem });
                return accu;
            }, []);
            const source = helper.getSourceToTry(logicalName, sources) || { url, crossOrigin: crossOrigin.ANONYMOUS };
            this.state = {
                crossOrigin: source.crossOrigin,
                imageSrc: source.url,
                sources,
            };
        }
    }

    onDragStart = (event) => {
        event.preventDefault();
        return false;
    };

    onError = (e) => {
        const { crossOrigin: origin, imageSrc, sources } = this.state;
        const { logicalName } = this.props.model;

        storeManager.dispatch(
            mediaResourcesActions.setErroredSource(logicalName, imageSrc, origin)
        );

        if (settingsReader.shouldUsePresignedUrl()) {
            console.error('presigned url image error', imageSrc, e); // No information about the actual error is available from the error event.
        }

        const nextSource = helper.getNextSource(sources, imageSrc, origin);
        if (nextSource) {
            this.setState({ imageSrc: nextSource.url, crossOrigin: nextSource.crossOrigin });
        } else {
            this.props.reportReady(false);
        }
    };

    onLoad = () => {
        const { imageSrc, crossOrigin: origin } = this.state;
        const { data, logicalName } = this.props.model;
        if (imageSrc !== data) {
            storeManager.dispatch(
                mediaResourcesActions.setLoadedSource(logicalName, imageSrc, origin)
            );
        }
    };

    render() {
        const { crossOrigin: origin } = this.state;
        const extraProps = {};
        if (ServiceWorkerService.browserSupportsServiceWorker()) {
            extraProps.crossOrigin = origin;
        }

        return (<Image
            autoScale={this.props.autoScale}
            contentId={this.state.imageSrc}
            extraProps={extraProps}
            isPreRender={this.props.isPreRender}
            isInteraction={this.props.isInteraction}
            model={this.props.model}
            onErrorCallback={this.onError}
            onLoadCallback={this.onLoad}
            questionObjectType={this.props.questionObjectType}
            reportReady={this.props.reportReady}
            src={this.state.imageSrc}
            useModelWidthAndHeight={this.props.useModelWidthAndHeight}
            usePaperBasedImageDimensions={this.props.usePaperBasedImageDimensions}
            useMCDiscreteWithOneImageMaxWidth={this.props.useMCDiscreteWithOneImageMaxWidth}
        />);
    }
}

export default PlainImage;
